import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Elements } from '@/components/elements';
import type { PlanWithAgePremiums } from '@/components/widgets/PlansSection';
import CloseIcon from '@/assets/icons/close.svg';
import { formatCurrencyWithCommas } from '@/utils/formatNumber';
import Image from 'next/image';

const MIN_PLAN_COLUMN_WIDTH = 365;

const Card = ({ children }: { children: React.ReactNode }) => (
    <div className="overflow-hidden rounded-2xl bg-white ring-1 ring-gray-200">
        {children}
    </div>
);

const IoTable = ({
    rows,
    plans,
    onScroll,
    scrollRef,
}: {
    rows: Array<{
        label: string;
        getValue: (plan: any) => { inNet?: string; outNet?: string };
    }>;
    plans: any[];
    onScroll?: (event: React.UIEvent<HTMLDivElement>) => void;
    scrollRef?: React.Ref<HTMLDivElement>;
}) => {
    const numPlans = plans.length;
    const minPlanColumnWidth = MIN_PLAN_COLUMN_WIDTH;

    // Generate grid template columns dynamically
    const gridStyle: React.CSSProperties = {
        gridTemplateColumns: `260px repeat(${numPlans}, minmax(${minPlanColumnWidth}px, 1fr))`,
    };
    const mobileGridStyle: React.CSSProperties = {
        gridTemplateColumns: `repeat(${numPlans}, minmax(${minPlanColumnWidth}px, 1fr))`,
    };

    return (
        <div
            ref={scrollRef}
            onScroll={onScroll}
            className="divide-y divide-gray-200 overflow-x-auto"
        >
            {/* Desktop table (md+) */}
            <div className="hidden md:block">
                {rows.map((r) => (
                    <div key={r.label} className="grid gap-3 text-base min-w-full" style={gridStyle}>
                        <div className="border-r border-gray-200 px-3 py-2 text-gray-700">
                            {r.label}
                        </div>
                        {plans.map((plan) => {
                            const value = r.getValue(plan);
                            const isCurrentPlan = (plan as any).isCurrentPlan === true;
                            return (
                                <div key={plan.id} className="px-3 py-2 border-r border-gray-200 last:border-r-0 min-w-0">
                                    <div className="grid grid-cols-[max-content_1fr] items-start gap-x-4 gap-y-1 text-sm">
                                        <span className="self-start text-base text-gray-450 whitespace-nowrap">In network</span>
                                        <span className="self-start text-gray-800 text-right min-w-0">
                                            {value.inNet ? (
                                                <span className="block max-w-full whitespace-normal wrap-break-word text-base">
                                                    {value.inNet}
                                                </span>
                                            ) : (
                                                <span className="text-gray-400 text-base">{isCurrentPlan ? 'N/A' : '—'}</span>
                                            )}
                                        </span>
                                        <span className="self-start text-base text-gray-450 whitespace-nowrap">Out-of-network</span>
                                        <span className="self-start text-gray-800 text-right min-w-0">
                                            {value.outNet ? (
                                                <span className="block max-w-full whitespace-normal wrap-break-word text-base">
                                                    {value.outNet}
                                                </span>
                                            ) : (
                                                <span className="text-gray-400 text-base">{isCurrentPlan ? 'N/A' : 'Not Covered'}</span>
                                            )}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>

            {/* Mobile (<md): no Benefit column; label inside each plan cell */}
            <div className="md:hidden">
                {rows.map((r) => (
                    <div key={r.label} className="grid gap-3 text-base min-w-full" style={mobileGridStyle}>
                        {plans.map((plan) => {
                            const value = r.getValue(plan);
                            const isCurrentPlan = (plan as any).isCurrentPlan === true;
                            return (
                                <div key={plan.id} className="px-3 py-2 border-r border-gray-200 last:border-r-0 min-w-0">
                                    <div className="text-gray-500 font-normal mb-2">
                                        {r.label}
                                    </div>
                                    <div className="grid grid-cols-[max-content_1fr] items-start gap-x-4 gap-y-1 text-base">
                                        <span className="self-start text-gray-450 whitespace-nowrap">In network</span>
                                        <span className="self-start text-gray-800 text-right min-w-0">
                                            {value.inNet ? (
                                                <span className="block max-w-full whitespace-normal wrap-break-word">
                                                    {value.inNet}
                                                </span>
                                            ) : (
                                                <span className="text-gray-400">{isCurrentPlan ? 'N/A' : '—'}</span>
                                            )}
                                        </span>
                                        <span className="self-start text-gray-450 whitespace-nowrap">Out-of-network</span>
                                        <span className="self-start text-gray-800 text-right min-w-0">
                                            {value.outNet ? (
                                                <span className="block max-w-full whitespace-normal wrap-break-word">
                                                    {value.outNet}
                                                </span>
                                            ) : (
                                                <span className="text-gray-400">{isCurrentPlan ? 'N/A' : 'Not Covered'}</span>
                                            )}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        </div>
    );
};

const parseInOut = (raw: string | undefined | null): { inNet?: string; outNet?: string } => {
    if (!raw || typeof raw !== 'string' || raw.trim() === '') {
        return {};
    }

    try {
        // Remove any pipe-separated additional info (like "| limit: 60 visit(s) per year")
        const [mainPart] = raw.split("|");

        // Split by "/" or " / " to separate in-network and out-of-network
        // Also handle escaped slashes that might be in JSON
        const normalized = mainPart.replace(/\\\//g, '/');
        const parts = normalized
            .split(/\s*\/\s*/)
            .map((s) => s.trim())
            .filter((s) => s.length > 0);

        if (parts.length === 0) {
            return {};
        }

        const clean = (part: string | undefined): string | undefined => {
            if (!part) return undefined;
            let cleaned = part
                // убрать текстовые префиксы
                .replace(/^In-?Network:\s*/i, "")
                .replace(/^Out[- ]of[- ]Network:\s*/i, "")
                .replace(/^In network:\s*/i, "")
                .replace(/^Out-of-network:\s*/i, "")
                // убрать хвосты вида "limit: see carrier documentation..."
                .replace(/limit:\s*see carrier documentation for more information.*$/i, "")
                .trim();

            // если осталось только "limit: ..." или пусто — не рисуем
            if (!cleaned || /^limit:/i.test(cleaned)) {
                return undefined;
            }

            return cleaned;
        };

        // Handle cases where there's only one part (in-network only) or two parts
        if (parts.length === 1) {
            const cleaned = clean(parts[0]);
            // If it starts with "In-Network" or "In network", it's in-network
            if (parts[0].match(/^In[- ]?Network:/i)) {
                return cleaned ? { inNet: cleaned } : {};
            }
            // Otherwise, assume it's a general value for in-network
            return cleaned ? { inNet: cleaned } : {};
        }

        // Handle two parts: in-network and out-of-network
        const [inPartRaw, outPartRaw] = parts;
        const inNet = clean(inPartRaw);
        const outNet = clean(outPartRaw);

        return {
            inNet: inNet || undefined,
            outNet: outNet || undefined,
        };
    } catch (error) {
        // If parsing fails, return empty object
        console.warn('Error parsing in/out network data:', raw, error);
        return {};
    }
};

// Helpers to work both with:
// - existing FullPlan shape (individual_medical_deductible, etc.)
// - compare API shape from /public/plans/medical/compare (deductibles, moop, benefits)

const toDisplayString = (value: unknown): string | undefined => {
    if (value === null || value === undefined) return undefined;
    if (typeof value === 'number') {
        return value.toLocaleString('en-US');
    }
    const trimmed = String(value).trim();
    return trimmed.length ? trimmed : undefined;
};

const getDeductibleValue = (
    plan: any,
    field: 'individual_medical' | 'family_medical' | 'individual_drug' | 'family_drug',
): { inNet?: string; outNet?: string } => {
    const d = plan?.deductibles as
        | {
            individual_medical?: number;
            family_medical?: number;
            individual_drug?: number;
            family_drug?: number;
            drug?: string | number;
        }
        | undefined;

    if (d) {
        if (field === 'individual_medical' && d.individual_medical !== undefined) {
            const v = toDisplayString(d.individual_medical);
            return v ? { inNet: v } : {};
        }
        if (field === 'family_medical' && d.family_medical !== undefined) {
            const v = toDisplayString(d.family_medical);
            return v ? { inNet: v } : {};
        }
        if (field === 'individual_drug') {
            if (d.individual_drug !== undefined) {
                const v = toDisplayString(d.individual_drug);
                return v ? { inNet: v } : {};
            }
            if (d.drug !== undefined) {
                const v = toDisplayString(d.drug);
                return v ? { inNet: v } : {};
            }
        }
        if (field === 'family_drug') {
            if (d.family_drug !== undefined) {
                const v = toDisplayString(d.family_drug);
                return v ? { inNet: v } : {};
            }
            if (d.drug !== undefined) {
                const v = toDisplayString(d.drug);
                return v ? { inNet: v } : {};
            }
        }
    }

    // Fallback to old FullPlan string fields
    if (field === 'individual_medical') {
        return parseInOut((plan as any).individual_medical_deductible);
    }
    if (field === 'family_medical') {
        return parseInOut((plan as any).family_medical_deductible);
    }
    if (field === 'individual_drug') {
        return parseInOut((plan as any).individual_drug_deductible);
    }
    if (field === 'family_drug') {
        return parseInOut((plan as any).family_drug_deductible);
    }

    return {};
};

const getMoopValue = (
    plan: any,
    field: 'individual' | 'family',
): { inNet?: string; outNet?: string } => {
    const m = plan?.moop as { individual?: number; family?: number } | undefined;

    if (m) {
        if (field === 'individual' && m.individual !== undefined) {
            const v = toDisplayString(m.individual);
            return v ? { inNet: v } : {};
        }
        if (field === 'family' && m.family !== undefined) {
            const v = toDisplayString(m.family);
            return v ? { inNet: v } : {};
        }
    }

    // Fallback to old FullPlan string fields
    if (field === 'individual') {
        return parseInOut((plan as any).individual_medical_moop);
    }
    if (field === 'family') {
        return parseInOut((plan as any).family_medical_moop);
    }

    return {};
};

const getBenefitValue = (
    plan: any,
    keys: string | string[],
): { inNet?: string; outNet?: string } => {
    const keyList = Array.isArray(keys) ? keys : [keys];
    const b = plan?.benefits || {};

    for (const k of keyList) {
        const raw = b[k] ?? (plan as any)[k];
        if (raw !== undefined && raw !== null) {
            const str = String(raw);
            // Если в строке уже зашиты In-Network / Out-of-Network, парсим её как in/out
            if (
                /In[- ]?Network:/i.test(str) ||
                /Out[- ]of[- ]Network:/i.test(str) ||
                str.includes('/')
            ) {
                const parsed = parseInOut(str);
                if (parsed.inNet || parsed.outNet) {
                    return parsed;
                }
            }

            const v = toDisplayString(raw);
            if (v) {
                return { inNet: v };
            }
        }
    }

    if (keyList.length > 0) {
        return parseInOut((plan as any)[keyList[0]]);
    }

    return {};
};


const SectionHeader = ({ title }: { title: string }) => (
    <div className="border-b border-blue-100 bg-blue-50 px-3 py-2 text-xl font-semibold text-gray-700">
        {title}
    </div>
);

const TableSkeleton = ({ numPlans, numRows = 4 }: { numPlans: number; numRows?: number }) => {
    const gridStyle: React.CSSProperties = {
        gridTemplateColumns: `260px repeat(${numPlans}, minmax(${MIN_PLAN_COLUMN_WIDTH}px, 1fr))`,
    };

    return (
        <div className="divide-y divide-gray-200 overflow-x-auto">
            {/* Desktop skeleton (md+) */}
            <div className="hidden md:block">
                {/* Header row skeleton */}
                <div className="grid gap-3 text-base min-w-full" style={gridStyle}>
                    <div className="border-r border-gray-200 px-3 py-2">
                        <div className="h-5 w-20 bg-gray-200 rounded animate-pulse" />
                    </div>
                    {Array.from({ length: numPlans }).map((_, idx) => (
                        <div key={idx} className="px-3 py-2 text-center border-r border-gray-200 last:border-r-0">
                            <div className="h-4 w-24 bg-gray-200 rounded animate-pulse mx-auto" />
                        </div>
                    ))}
                </div>
                {/* Rows skeleton */}
                {Array.from({ length: numRows }).map((_, rowIdx) => (
                    <div key={rowIdx} className="grid gap-3 text-base min-w-full" style={gridStyle}>
                        <div className="border-r border-gray-200 px-3 py-2">
                            <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
                        </div>
                        {Array.from({ length: numPlans }).map((_, colIdx) => (
                            <div key={colIdx} className="px-3 py-2 border-r border-gray-200 last:border-r-0">
                                <div className="flex justify-between gap-4 text-sm">
                                    <div className="flex flex-col text-gray-500 gap-1">
                                        <div className="h-3 w-16 bg-gray-200 rounded animate-pulse" />
                                        <div className="h-3 w-20 bg-gray-200 rounded animate-pulse" />
                                    </div>
                                    <div className="flex flex-col text-right gap-1">
                                        <div className="h-3 w-20 bg-gray-200 rounded animate-pulse ml-auto" />
                                        <div className="h-3 w-16 bg-gray-200 rounded animate-pulse ml-auto" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ))}
            </div>

            {/* Mobile skeleton (<md): no Benefit column, title inside each plan column */}
            <div className="md:hidden">
                {Array.from({ length: numRows }).map((_, rowIdx) => (
                    <div
                        key={rowIdx}
                        className="grid gap-3 text-base min-w-full border-b border-gray-200 py-3"
                        style={{ gridTemplateColumns: `repeat(${numPlans}, minmax(0, 1fr))` }}
                    >
                        {Array.from({ length: numPlans }).map((_, colIdx) => (
                            <div key={colIdx} className="px-3 py-2 border-r border-gray-200 last:border-r-0">
                                <div className="h-4 w-24 bg-gray-200 rounded animate-pulse mb-2" />
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                    <div className="h-3 w-16 bg-gray-200 rounded animate-pulse" />
                                    <div className="h-3 w-20 bg-gray-200 rounded animate-pulse ml-auto" />
                                    <div className="h-3 w-20 bg-gray-200 rounded animate-pulse" />
                                    <div className="h-3 w-16 bg-gray-200 rounded animate-pulse ml-auto" />
                                </div>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
};

export interface CompareModalProps {
    isOpen: boolean;
    onClose: () => void;
    comparePlans: PlanWithAgePremiums[];
    familyAges?: string[];
    isStateCountrySearch?: boolean;
    employerContributionAmount?: number | null;
    /** Used by View All Rates modal / embedded plan card for compare controls. */
    onToggleComparePlan?: (plan: PlanWithAgePremiums) => void;
}

export const CompareModal: React.FC<CompareModalProps> = ({
    isOpen,
    onClose,
    comparePlans,
    familyAges = [],
    isStateCountrySearch = false,
    employerContributionAmount = null,
    onToggleComparePlan,
}) => {
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const cardsScrollRef = useRef<HTMLDivElement | null>(null);
    const desktopCardsScrollRef = useRef<HTMLDivElement | null>(null);
    const tableScrollRefs = useRef<Array<HTMLDivElement | null>>([]);
    const isSyncingRef = useRef(false);
    const [detailedPlans, setDetailedPlans] = useState<any[]>([]);
    const [isLoadingDetails, setIsLoadingDetails] = useState(false);
    const [detailsError, setDetailsError] = useState<string | null>(null);
    const [isDesktopStickyHeader, setIsDesktopStickyHeader] = useState(false);
    const [isDesktopViewport, setIsDesktopViewport] = useState(() => {
        if (typeof window === 'undefined') return true;
        return window.innerWidth >= 1024;
    });
    // Lock background page scroll while modal is open (prevents double scrollbars and scrolling behind modal)
    useEffect(() => {
        if (typeof document === 'undefined') return;
        if (!isOpen) return;

        const body = document.body;
        const html = document.documentElement;
        const prevBodyOverflow = body.style.overflow;
        const prevBodyPaddingRight = body.style.paddingRight;
        const prevHtmlOverflow = html.style.overflow;

        // Compensate for scrollbar removal to avoid layout shift
        const scrollbarWidth = window.innerWidth - html.clientWidth;
        body.style.overflow = 'hidden';
        html.style.overflow = 'hidden';
        if (scrollbarWidth > 0) {
            body.style.paddingRight = `${scrollbarWidth}px`;
        }

        return () => {
            body.style.overflow = prevBodyOverflow;
            body.style.paddingRight = prevBodyPaddingRight;
            html.style.overflow = prevHtmlOverflow;
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || comparePlans.length === 0) {
            return;
        }

        const controller = new AbortController();

        const fetchDetails = async () => {
            try {
                setIsLoadingDetails(true);
                setDetailsError(null);

                const realPlans = comparePlans.filter((p) => !(p as any).isCurrentPlan);
                if (realPlans.length === 0) {
                    setDetailedPlans([]);
                    return;
                }

                const idsParam = realPlans.map((p) => p.id).join(',');
                const url = `https://rates-public.predictablebenefits.com/public/plans/medical/compare?ids=${encodeURIComponent(
                    idsParam,
                )}`;

                const res = await fetch(url, {
                    method: 'GET',
                    signal: controller.signal,
                });

                if (!res.ok) {
                    throw new Error('Failed to load comparison details');
                }

                const data = await res.json();

                const plansFromApi: any[] = Array.isArray(data)
                    ? data
                    : Array.isArray((data as any).plans)
                        ? (data as any).plans
                        : [];

                setDetailedPlans(plansFromApi);
            } catch (err: any) {
                if (err?.name === 'AbortError') return;
                console.error('Error fetching compare details', err);
                setDetailsError('Unable to load detailed benefit information for comparison.');
                setDetailedPlans([]);
            } finally {
                setIsLoadingDetails(false);
            }
        };

        fetchDetails();

        return () => {
            controller.abort();
        };
    }, [isOpen, comparePlans]);

    const plansForTables = useMemo(() => {
        if (detailedPlans.length === 0) return comparePlans;
        const byId = new Map<string, any>(detailedPlans.map((p) => [p.id, p]));
        return comparePlans.map((p) => byId.get(p.id) ?? p);
    }, [comparePlans, detailedPlans]);

    const cardsGridStyle: React.CSSProperties = {
        gridTemplateColumns: `260px repeat(${comparePlans.length}, minmax(${MIN_PLAN_COLUMN_WIDTH}px, 1fr))`,
    };

    const syncScrollLeft = (source: HTMLDivElement | null, targets: Array<HTMLDivElement | null>) => {
        if (!source) return;
        if (isSyncingRef.current) return;
        isSyncingRef.current = true;
        const nextLeft = source.scrollLeft;
        targets.forEach((target) => {
            if (target && target !== source) {
                target.scrollLeft = nextLeft;
            }
        });
        requestAnimationFrame(() => {
            isSyncingRef.current = false;
        });
    };

    const handleCardsScroll = () => {
        if (!cardsScrollRef.current) return;
        syncScrollLeft(cardsScrollRef.current, tableScrollRefs.current);
    };

    const handleDesktopCardsScroll = () => {
        if (!desktopCardsScrollRef.current) return;
        syncScrollLeft(desktopCardsScrollRef.current, tableScrollRefs.current);
    };

    const handleTableScroll = (event: React.UIEvent<HTMLDivElement>) => {
        const source = event.currentTarget as HTMLDivElement;
        const targets = [cardsScrollRef.current, desktopCardsScrollRef.current, ...tableScrollRefs.current];
        syncScrollLeft(source, targets);
    };

    const registerTableRef = (index: number) => (el: HTMLDivElement | null) => {
        tableScrollRefs.current[index] = el;
    };

    const setCardsScrollContainer = (el: HTMLDivElement | null) => {
        cardsScrollRef.current = el;
    };

    const setDesktopCardsScrollContainer = (el: HTMLDivElement | null) => {
        desktopCardsScrollRef.current = el;
    };

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const onResize = () => {
            setIsDesktopViewport(window.innerWidth >= 1024);
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    useEffect(() => {
        if (!isOpen) return;
        if (!isDesktopViewport) {
            setIsDesktopStickyHeader(false);
            return;
        }
        const el = scrollContainerRef.current;
        if (!el) return;

        let raf = 0;
        const onScroll = () => {
            if (raf) return;
            raf = window.requestAnimationFrame(() => {
                raf = 0;
                // Show compact sticky header only after the user scrolls down a bit
                setIsDesktopStickyHeader(el.scrollTop > 0);
            });
        };

        el.addEventListener('scroll', onScroll, { passive: true });
        onScroll();

        return () => {
            el.removeEventListener('scroll', onScroll);
            if (raf) window.cancelAnimationFrame(raf);
        };
    }, [isDesktopViewport, isOpen]);

    const handleDesktopHeaderWheel = (e: React.WheelEvent) => {
        // Desktop UX: user can scroll while hovering the fixed header (title/cards),
        // and the scroll should move the tables container below.
        if (!isDesktopViewport) return;
        const scroller = scrollContainerRef.current;
        if (!scroller) return;
        if (e.deltaY === 0) return;
        scroller.scrollTop += e.deltaY;
    };

    const renderCompactCard = (plan: PlanWithAgePremiums) => {
        const name = plan.display_name || plan.name || plan.id;
        const toTitleCase = (input?: string) => {
            const s = (input ?? '').trim();
            if (!s) return '';
            return s
                .replace(/\s+/g, ' ')
                .split(' ')
                .map((word) => {
                    const lower = word.toLowerCase();
                    return lower ? lower[0].toUpperCase() + lower.slice(1) : lower;
                })
                .join(' ');
        };
        const identifierValue =
            plan.identifiers?.find((id) => id.type === 'hios_id')?.value ?? plan.identifiers?.[0]?.value;
        const hiosId = identifierValue && identifierValue.length > 5 ? identifierValue : plan.id;
        const premiumStr = plan.premium != null ? String(plan.premium) : '';
        const premiumNum = parseFloat(premiumStr.replace(/[^0-9.]/g, '')) || 0;
        const premium = premiumNum > 0 ? formatCurrencyWithCommas(premiumNum) : '—';
        const isCurrentPlan = (plan as any).isCurrentPlan === true;
        const hasEmployerContribution = premiumNum > 0 && employerContributionAmount != null && employerContributionAmount > 0;
        const employerPay = hasEmployerContribution ? Math.min(employerContributionAmount!, premiumNum) : 0;
        const employeePay = hasEmployerContribution ? Math.max(premiumNum - employerContributionAmount!, 0) : 0;
        void plan;

        return (
            <article className="rounded-2xl border border-brand-700 bg-white w-[365px] flex flex-col h-full overflow-hidden">
                {/* top: logo + tags */}
                <div
                    className={`bg-gray-50 px-3 py-2 flex flex-row items-center justify-between gap-3 ${
                        isCurrentPlan ? 'h-[112px]' : ''
                    }`}
                >
                    <div className="flex items-center gap-3 min-w-0">
                        {isCurrentPlan ? (
                            <div className="min-w-0">
                                <div className="flex flex-col min-w-0 gap-1">
                                    <div
                                        className="text-base font-medium text-gray-900 line-clamp-1"
                                        title={toTitleCase(plan.carrier?.name_long || plan.carrier?.name)}
                                    >
                                        {toTitleCase(plan.carrier?.name_long || plan.carrier?.name)}
                                    </div>
                                </div>
                            </div>
                        ) : plan.carrier?.logo_url ? (
                            <img
                                src={plan.carrier.logo_url}
                                alt={plan.carrier.name || 'Carrier logo'}
                                className="h-[100px] w-[100px] rounded object-contain shrink-0"
                                loading="lazy"
                                decoding="async"
                            />
                        ) : null}
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                        <Elements.ParsePlanType type={plan.level} />
                        {!isCurrentPlan && (
                        <Elements.ParseExchange on_market={plan.on_market} off_market={plan.off_market} />
                        )}
                    </div>
                </div>
                {/* bottom: plan name + premium */}
                <div className="bg-brand-50 px-3 py-3 flex flex-1 flex-col justify-between">
                    {!isCurrentPlan && (
                        <div className="flex flex-col min-w-0">
                            <div className="flex items-center justify-between gap-2">
                                <div className="text-sm font-normal text-gray-450">Plan Name</div>
                                <div className="text-sm font-medium whitespace-nowrap text-gray-450">
                                    HIOS ID: <span className="text-gray-450 font-medium">{hiosId}</span>
                                </div>
                            </div>
                            <div
                                className="text-base font-medium text-gray-900 wrap-break-word whitespace-normal line-clamp-3"
                                title={name}
                            >
                                {name}
                            </div>
                        </div>
                    )}

                    {isCurrentPlan && (
                        <div className="flex flex-col min-w-0 mb-3">
                            <div className="text-sm font-normal text-gray-450">Plan Name</div>
                            <div
                                className="text-base font-medium text-gray-900 wrap-break-word whitespace-normal line-clamp-2"
                                title={toTitleCase(name)}
                            >
                                {toTitleCase(name)}
                            </div>
                        </div>
                    )}

                    <div className="w-full rounded-2xl mt-2">
                        <div className="flex items-center justify-start">
                            {isStateCountrySearch && (plan.premiumAge18 || plan.premiumAge65) ? (
                                <div className="w-full">
                                    <div className="flex w-full items-center">
                                        <div className="flex items-center gap-3">
                                        {/* Age 18 */}
                                            <div className="relative flex flex-none h-[56px] w-fit flex-col items-start justify-center gap-0 rounded-lg bg-white px-3">
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    padding: 1,
                                                    borderRadius: 8,
                                                    background: 'linear-gradient(143.77deg, #0277E0 0%, #1357B0 101.42%)',
                                                    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                                    WebkitMaskComposite: 'xor',
                                                    maskComposite: 'exclude',
                                                    pointerEvents: 'none',
                                                }}
                                            />
                                            <span className="relative text-sm font-normal text-gray-500 whitespace-nowrap">
                                                Age 18
                                            </span>
                                            <span className="relative text-center text-lg font-medium text-primary-500 leading-none">
                                                {plan.premiumAge18
                                                    ? `${formatCurrencyWithCommas(
                                                          parseFloat(String(plan.premiumAge18).replace(/[^0-9.]/g, '')) || 0
                                                      )}`
                                                    : '—'}
                                            </span>
                                        </div>

                                        <span className="shrink-0 text-sm text-gray-900" aria-hidden>
                                            to
                                        </span>

                                        {/* Age 65 */}
                                            <div className="relative flex flex-none h-[56px] w-fit flex-col items-start justify-center gap-0 rounded-lg bg-white px-3">
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    padding: 1,
                                                    borderRadius: 8,
                                                    background: 'linear-gradient(143.77deg, #0277E0 0%, #1357B0 101.42%)',
                                                    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                                    WebkitMaskComposite: 'xor',
                                                    maskComposite: 'exclude',
                                                    pointerEvents: 'none',
                                                }}
                                            />
                                            <span className="relative text-sm font-normal text-gray-500 whitespace-nowrap">
                                                Age 65
                                            </span>
                                            <span className="relative text-center text-lg font-medium text-primary-500 leading-none">
                                                {plan.premiumAge65
                                                    ? `${formatCurrencyWithCommas(
                                                          parseFloat(String(plan.premiumAge65).replace(/[^0-9.]/g, '')) || 0
                                                      )}`
                                                    : '—'}
                                            </span>
                                            </div>
                                        </div>

                                        <div className="ml-auto flex shrink-0 flex-col items-end text-base font-medium text-gray-800 leading-tight text-right">
                                            <span className="whitespace-nowrap">Monthly</span>
                                            <span className="whitespace-nowrap">Premium</span>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                hasEmployerContribution ? (
                                    <div className="w-full">
                                        <div className="flex w-full flex-row flex-nowrap items-start justify-start gap-2">
                                            {/* Monthly Premium */}
                                            <div className="flex flex-1 min-w-0 flex-col items-stretch justify-start">
                                                <div className="relative flex h-[43px] w-full min-w-0 items-center justify-center px-2 rounded-lg bg-white">
                                                    <div
                                                        style={{
                                                            position: 'absolute',
                                                            inset: 0,
                                                            padding: 1,
                                                            borderRadius: 8,
                                                            background: 'linear-gradient(143.77deg, #0277E0 0%, #1357B0 101.42%)',
                                                            WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                                            WebkitMaskComposite: 'xor',
                                                            maskComposite: 'exclude',
                                                            pointerEvents: 'none',
                                                        }}
                                                    />
                                                    <span className="relative w-full min-w-0 truncate text-center text-sm font-medium text-primary-500 sm:text-base tabular-nums">
                                                        {formatCurrencyWithCommas(premiumNum)}
                                                    </span>
                                                </div>
                                                <div className="mt-1 w-full text-xs font-normal text-center leading-tight text-gray-900">
                                                    Premium
                                                </div>
                                            </div>

                                            <div className="h-[43px] flex items-center">
                                                <span className="text-base font-medium text-gray-700">-</span>
                                            </div>

                                            {/* Employer */}
                                            <div className="flex flex-1 min-w-0 flex-col items-stretch justify-start">
                                                <div className="relative flex h-[43px] w-full min-w-0 items-center justify-center px-2 rounded-lg bg-brand-100">
                                                    <div
                                                        style={{
                                                            position: 'absolute',
                                                            inset: 0,
                                                            padding: 1,
                                                            borderRadius: 8,
                                                            background: 'transparent',
                                                            WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                                            WebkitMaskComposite: 'xor',
                                                            maskComposite: 'exclude',
                                                            pointerEvents: 'none',
                                                        }}
                                                    />
                                                    <span className="relative w-full min-w-0 truncate text-center text-sm font-medium text-gray-900 sm:text-base tabular-nums">
                                                        {formatCurrencyWithCommas(employerPay)}
                                                    </span>
                                                </div>
                                                <div className="mt-1 w-full text-[11px] font-normal text-center leading-tight text-gray-900">
                                                    Employer
                                                </div>
                                            </div>

                                            <div className="h-[43px] flex items-center">
                                                <span className="text-base font-medium text-gray-700">=</span>
                                            </div>

                                            {/* Employee */}
                                            <div className="flex flex-1 min-w-0 flex-col items-stretch justify-start">
                                                <div className="relative flex h-[43px] w-full min-w-0 items-center justify-center px-2 rounded-lg bg-white border border-brand-100">
                                                    <span className="relative w-full min-w-0 truncate text-center text-sm font-medium text-gray-900 sm:text-base tabular-nums">
                                                        {formatCurrencyWithCommas(employeePay)}
                                                    </span>
                                                </div>
                                                <div className="mt-1 w-full text-xs font-normal text-center leading-tight text-gray-900">
                                                    Employee
                                                </div>
                                            </div>

                                        </div>
                                    </div>
                                ) : (
                                    <div className="relative flex h-[35px] w-full items-center justify-start rounded-lg">
                                        {/* Match ComparePlanCard "single premium" UI */}
                                        <div className="w-full rounded-2xl">
                                            <div className="flex flex-row items-center justify-start gap-[20px]">
                                                <div className="relative flex w-full flex-1 flex-row items-center justify-start gap-[20px]">
                                                    <div className="relative flex flex-1 h-[35px] items-center justify-center px-4 rounded-lg bg-white sm:w-[150px] sm:flex-none">
                                                        <div
                                                            style={{
                                                                position: 'absolute',
                                                                inset: 0,
                                                                padding: 1,
                                                                borderRadius: 8,
                                                                background: 'linear-gradient(143.77deg, #0277E0 0%, #1357B0 101.42%)',
                                                                WebkitMask:
                                                                    'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                                                WebkitMaskComposite: 'xor',
                                                                maskComposite: 'exclude',
                                                                pointerEvents: 'none',
                                                            }}
                                                        />
                                                        <span className="relative text-center text-base font-medium text-primary-500 sm:text-lg">
                                                            {premium}
                                                        </span>
                                                    </div>
                                                    <span className="text-left text-base font-medium text-gray-800 shrink-0">
                                                        {familyAges.length > 1 ? (
                                                            <span>
                                                                <span>Family</span> <span>Monthly Premium</span>
                                                            </span>
                                                        ) : (
                                                            <span>
                                                                <span>Monthly</span> <span>Premium</span>
                                                            </span>
                                                        )}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                </div>
            </article>
        );
    };


    if (!isOpen || comparePlans.length === 0) return null;

    return (
        <>
        <div className="fixed inset-0 z-80 flex items-end justify-center ">
            <div
                className="fixed inset-0 bg-black/50 "
                onClick={onClose}
            />
                <div className="relative z-81 w-full max-w-none h-[90vh] rounded-t-3xl bg-white  flex flex-col overflow-hidden">
                {/* Fixed header block (always visible) */}
                <div className="shrink-0 bg-white p-6 " onWheel={handleDesktopHeaderWheel}>
                    <div className="mb-6 flex items-center justify-center relative">
                        <h2 className="text-3xl md:text-6xl leading-tight font-semibold text-gray-900 text-center">
                            Compare Plans
                        </h2>
                        <button
                            type="button"
                            onClick={onClose}
                            className="absolute right-0 top-1/2 -translate-y-1/2 flex h-14 w-14 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                            aria-label="Close compare modal"
                        >
                            <Image src={CloseIcon} alt="Close" width={28} height={28} />
                        </button>
                    </div>

                    <div>
                        {/* Desktop: cards row is fixed; switches to compact when tables are scrolled */}
                        <div
                            className="hidden lg:block -mx-6 px-6 "
                            // Bottom-only shadow (no halo on top)
                        >
                            <div
                                ref={setDesktopCardsScrollContainer}
                                onScroll={handleDesktopCardsScroll}
                                // Allow horizontal scroll here too (important for zoom / narrow viewports on desktop).
                                className={`overflow-x-auto pointer-events-auto overscroll-x-contain transition-all [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${isDesktopStickyHeader ? 'py-3' : ''}`}
                            >
                                <div className="grid gap-3 min-w-full " style={cardsGridStyle}>
                                    <div />
                                    {comparePlans.map((plan) => (
                                        <div key={plan.id} className="flex justify-center items-stretch">
                                            {isDesktopStickyHeader ? (
                                                renderCompactCard(plan)
                                            ) : (
                                                <Elements.ComparePlanCard
                                                    plan={plan}
                                                    familyAges={familyAges}
                                                    isStateCountrySearch={isStateCountrySearch}
                                                    employerContributionAmount={employerContributionAmount}
                                                    onToggleCompare={() => onToggleComparePlan?.(plan)}
                                                    isCompared
                                                    comparePosition={comparePlans.findIndex((p) => p.id === plan.id) + 1}
                                                    compareTotal={3}
                                                />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Phone: full cards slider */}
                        <div
                            className="md:hidden overflow-x-auto pb-4 -mx-6 px-6 snap-x snap-mandatory"
                            style={{ scrollPaddingLeft: 'calc(50% - 182.5px)', scrollPaddingRight: 'calc(50% - 182.5px)' }}
                            ref={setCardsScrollContainer}
                            onScroll={handleCardsScroll}
                        >
                            <div className="flex gap-4">
                                {comparePlans.map((plan, index) => (
                                    <div
                                        key={plan.id}
                                        className="shrink-0 snap-center flex items-stretch"
                                        style={{
                                            paddingLeft: index === 0 ? 'calc(50% - 182.5px)' : '0',
                                            paddingRight: index === comparePlans.length - 1 ? 'calc(50% - 182.5px)' : '0',
                                        }}
                                    >
                                        <Elements.ComparePlanCard
                                            plan={plan}
                                            familyAges={familyAges}
                                            isStateCountrySearch={isStateCountrySearch}
                                            employerContributionAmount={employerContributionAmount}
                                            onToggleCompare={() => onToggleComparePlan?.(plan)}
                                            isCompared
                                            comparePosition={comparePlans.findIndex((p) => p.id === plan.id) + 1}
                                            compareTotal={3}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Tablet: compact cards slider */}
                        <div
                            className="hidden md:block lg:hidden overflow-x-auto pb-4 -mx-6 px-6 snap-x snap-mandatory"
                            style={{ scrollPaddingLeft: 'calc(50% - 127.5px)', scrollPaddingRight: 'calc(50% - 127.5px)' }}
                            ref={setCardsScrollContainer}
                            onScroll={handleCardsScroll}
                        >
                            <div className="flex gap-4">
                                {comparePlans.map((plan, index) => (
                                    <div
                                        key={plan.id}
                                    className="shrink-0 snap-center flex items-stretch"
                                        style={{
                                            paddingLeft: index === 0 ? 'calc(50% - 127.5px)' : '0',
                                            paddingRight: index === comparePlans.length - 1 ? 'calc(50% - 127.5px)' : '0',
                                        }}
                                    >
                                        {renderCompactCard(plan)}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Scrollable tables section */}
                <div ref={scrollContainerRef} className="flex-1 min-h-0 overflow-y-auto px-6 pb-6">
                    <div className="flex flex-col gap-4 mt-6">
                    {detailsError && (
                        <div className="text-sm text-red-500 px-1">{detailsError}</div>
                    )}
                    {isLoadingDetails ? (
                        <>
                            <Card>
                                <SectionHeader title="Deductibles" />
                                <TableSkeleton numPlans={comparePlans.length} numRows={4} />
                            </Card>
                            <Card>
                                <SectionHeader title="Max Out-of-pocket" />
                                <TableSkeleton numPlans={comparePlans.length} numRows={4} />
                            </Card>
                            <Card>
                                <SectionHeader title="Doctor Visits" />
                                <TableSkeleton numPlans={comparePlans.length} numRows={3} />
                            </Card>
                            <Card>
                                <SectionHeader title="Medication" />
                                <TableSkeleton numPlans={comparePlans.length} numRows={4} />
                            </Card>
                            <Card>
                                <SectionHeader title="Hospital & Emergency Care" />
                                <TableSkeleton numPlans={comparePlans.length} numRows={11} />
                            </Card>
                            <Card>
                                <SectionHeader title="Pediatric Coverage" />
                                <TableSkeleton numPlans={comparePlans.length} numRows={3} />
                            </Card>
                            <Card>
                                <SectionHeader title="Mental health & substance care" />
                                <TableSkeleton numPlans={comparePlans.length} numRows={4} />
                            </Card>
                            <Card>
                                <SectionHeader title="Pregnancy & Birth" />
                                <TableSkeleton numPlans={comparePlans.length} numRows={4} />
                            </Card>
                            <Card>
                                <SectionHeader title="Rehabilitation" />
                                <TableSkeleton numPlans={comparePlans.length} numRows={2} />
                            </Card>
                        </>
                    ) : (
                        <>
                            <Card>
                                <SectionHeader title="Deductibles" />
                                <IoTable
                                    plans={plansForTables}
                                    onScroll={handleTableScroll}
                                    scrollRef={registerTableRef(0)}
                                    rows={[
                                        {
                                            label: "Individual Medical",
                                            getValue: (plan) => getDeductibleValue(plan, "individual_medical"),
                                        },
                                        {
                                            label: "Individual Drug",
                                            getValue: (plan) => getDeductibleValue(plan, "individual_drug"),
                                        },
                                        {
                                            label: "Family Medical",
                                            getValue: (plan) => getDeductibleValue(plan, "family_medical"),
                                        },
                                        {
                                            label: "Family Drug",
                                            getValue: (plan) => getDeductibleValue(plan, "family_drug"),
                                        },
                                    ]}
                                />
                            </Card>

                            <Card>
                                <SectionHeader title="Max Out-of-pocket" />
                                <IoTable
                                    plans={plansForTables}
                                    onScroll={handleTableScroll}
                                    scrollRef={registerTableRef(1)}
                                    rows={[
                                        {
                                            label: "Individual Medical",
                                            getValue: (plan) => getMoopValue(plan, "individual"),
                                        },
                                        {
                                            label: "Individual Drug",
                                            getValue: (plan) => parseInOut((plan as any).individual_drug_moop),
                                        },
                                        {
                                            label: "Family Medical",
                                            getValue: (plan) => getMoopValue(plan, "family"),
                                        },
                                        {
                                            label: "Family Drug",
                                            getValue: (plan) => parseInOut((plan as any).family_drug_moop),
                                        },
                                    ]}
                                />
                            </Card>
                            <Card>
                                <SectionHeader title="Doctor Visits" />
                                <IoTable
                                    plans={plansForTables}
                                    onScroll={handleTableScroll}
                                    scrollRef={registerTableRef(2)}
                                    rows={[
                                        {
                                            label: "Preventive Care",
                                            getValue: (plan) => getBenefitValue(plan, ["preventative_care", "preventive_care"])
                                        },
                                        {
                                            label: "Primary Care",
                                            getValue: (plan) => getBenefitValue(plan, ["primary_care", "primary_care_physician"]),
                                        },
                                        {
                                            label: "Specialist",
                                            getValue: (plan) => getBenefitValue(plan, "specialist")
                                        },
                                    ]}
                                />
                            </Card>
                            <Card>
                                <SectionHeader title="Medication" />
                                <IoTable
                                    plans={plansForTables}
                                    onScroll={handleTableScroll}
                                    scrollRef={registerTableRef(3)}
                                    rows={[
                                        {
                                            label: "Generic Drugs",
                                            getValue: (plan) => getBenefitValue(plan, "generic_drugs")
                                        },
                                        {
                                            label: "Preferred Brand Drugs",
                                            getValue: (plan) => parseInOut((plan as any).preferred_brand_drugs),
                                        },
                                        {
                                            label: "Non-preferred Brand Drugs",
                                            getValue: (plan) => parseInOut((plan as any).non_preferred_brand_drugs),
                                        },
                                        {
                                            label: "Specialty Drugs",
                                            getValue: (plan) => parseInOut((plan as any).specialty_drugs)
                                        },
                                    ]}
                                />
                            </Card>
                            <Card>
                                <SectionHeader title="Hospital & Emergency Care" />
                                <IoTable
                                    plans={plansForTables}
                                    onScroll={handleTableScroll}
                                    scrollRef={registerTableRef(4)}
                                    rows={[
                                        {
                                            label: "Urgent Care",
                                            getValue: (plan) => getBenefitValue(plan, "urgent_care")
                                        },
                                        {
                                            label: "Emergency Room",
                                            getValue: (plan) => getBenefitValue(plan, ["emergency", "emergency_room"])
                                        },
                                        {
                                            label: "Ambulance",
                                            getValue: (plan) => parseInOut((plan as any).ambulance)
                                        },
                                        {
                                            label: "Outpatient Care Center",
                                            getValue: (plan) => parseInOut(
                                                (plan as any).outpatient_ambulatory_care_center ||
                                                (plan as any).outpatient_facility
                                            ),
                                        },
                                        {
                                            label: "Facility (Outpatient)",
                                            getValue: (plan) => parseInOut((plan as any).outpatient_facility),
                                        },
                                        {
                                            label: "Physician (Outpatient)",
                                            getValue: (plan) => parseInOut((plan as any).outpatient_physician),
                                        },
                                        {
                                            label: "Facility (Inpatient)",
                                            getValue: (plan) => parseInOut((plan as any).inpatient_facility),
                                        },
                                        {
                                            label: "Physician (Inpatient)",
                                            getValue: (plan) => parseInOut((plan as any).inpatient_physician),
                                        },
                                        {
                                            label: "Skilled Nursing",
                                            getValue: (plan) => parseInOut((plan as any).skilled_nursing)
                                        },
                                        {
                                            label: "Home Health Care",
                                            getValue: (plan) => parseInOut((plan as any).home_health_care)
                                        },
                                        {
                                            label: "Hospice",
                                            getValue: (plan) => parseInOut((plan as any).hospice_service)
                                        },
                                    ]}
                                />
                            </Card>

                            <Card>
                                <SectionHeader title="Pediatric Coverage" />
                                <IoTable
                                    plans={plansForTables}
                                    onScroll={handleTableScroll}
                                    scrollRef={registerTableRef(5)}
                                    rows={[
                                        {
                                            label: "Child Dental",
                                            getValue: (plan) => getBenefitValue(plan, "child_dental")
                                        },
                                        {
                                            label: "Child Eye Exam",
                                            getValue: (plan) => getBenefitValue(plan, "child_eye_exam")
                                        },
                                        {
                                            label: "Child Eyewear",
                                            getValue: (plan) => getBenefitValue(plan, "child_eyewear")
                                        },
                                    ]}
                                />
                            </Card>

                            <Card>
                                <SectionHeader title="Mental health & substance care" />
                                <IoTable
                                    plans={plansForTables}
                                    onScroll={handleTableScroll}
                                    scrollRef={registerTableRef(6)}
                                    rows={[
                                        {
                                            label: "Mental Health (Outpatient)",
                                            getValue: (plan) => getBenefitValue(plan, "outpatient_mental_health"),
                                        },
                                        {
                                            label: "Mental Health (Inpatient)",
                                            getValue: (plan) => getBenefitValue(plan, "inpatient_mental_health"),
                                        },
                                        {
                                            label: "Substance Care (Outpatient)",
                                            getValue: (plan) => getBenefitValue(plan, "outpatient_substance"),
                                        },
                                        {
                                            label: "Substance Care (Inpatient)",
                                            getValue: (plan) => getBenefitValue(plan, "inpatient_substance"),
                                        },
                                    ]}
                                />
                            </Card>

                            <Card>
                                <SectionHeader title="Pregnancy & Birth" />
                                <IoTable
                                    plans={plansForTables}
                                    onScroll={handleTableScroll}
                                    scrollRef={registerTableRef(7)}
                                    rows={[
                                        {
                                            label: "Birth",
                                            getValue: (plan) => getBenefitValue(plan, "inpatient_birth")
                                        },
                                        {
                                            label: "Birth Physician",
                                            getValue: (plan) => getBenefitValue(plan, "inpatient_birth_physician"),
                                        },
                                        {
                                            label: "Prenatal Care",
                                            getValue: (plan) => {
                                                return getBenefitValue(plan, ["prenatal_care", "prenatal_postnatal_care"]);
                                            }
                                        },
                                        {
                                            label: "Postnatal Care",
                                            getValue: (plan) => {
                                                return getBenefitValue(plan, ["postnatal_care", "prenatal_postnatal_care"]);
                                            }
                                        },
                                    ]}
                                />
                            </Card>

                            <Card>
                                <SectionHeader title="Rehabilitation" />
                                <IoTable
                                    plans={plansForTables}
                                    onScroll={handleTableScroll}
                                    scrollRef={registerTableRef(8)}
                                    rows={[
                                        {
                                            label: "Rehabilitation",
                                            getValue: (plan) => getBenefitValue(plan, "rehabilitation_services"),
                                        },
                                        {
                                            label: "Habilitation",
                                            getValue: (plan) => getBenefitValue(plan, "habilitation_services"),
                                        },
                                    ]}
                                />
                            </Card>
                        </>
                    )}
                    </div>
                </div>
            </div>
        </div>
        </>
    );
};
